import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box, CircularProgress, InputAdornment, Popover, Stack, TextField, Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import { useQuery } from '@tanstack/react-query'
import type { LocationTreeNode } from '@cmms/types'

import { api } from '../lib/api'

/**
 * Hierarchical location picker for create-WO / create-task dialogs.
 * Fetches the site's location tree lazily and renders it as an
 * expand/collapse dropdown so the user can drill into the parent
 * hierarchy instead of hunting through a flat autocomplete list.
 *
 * Selection behaviour:
 *   • Any node is selectable (site root → building → floor → room).
 *   • Selecting a node closes the popover and reports its id via
 *     `onChange`. Clearing (× button on the trigger) reports "".
 *   • Ancestors of the current value auto-expand so the highlighted
 *     row is visible when the popover opens.
 *   • Under an active search query, every branch containing a match
 *     auto-expands and non-matching leaf branches collapse to a
 *     zero-height Fragment.
 *
 * Falls back to a plain disabled input while `siteId` is null — the
 * consumer's site picker owns that state.
 */

export function LocationTreeSelect({
  siteId, value, onChange, disabled, placeholder,
}: {
  siteId: string | null
  value: string
  onChange: (id: string) => void
  disabled?: boolean
  placeholder?: string
}) {
  const anchorRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  // Lazy tree fetch — only fires when the user actually opens the
  // popover, or when we need the label of the currently-selected id.
  // Keyed by siteId so switching sites drops the previous cache and
  // fetches fresh under the new scope.
  const treeQ = useQuery({
    queryKey: ['location-tree', siteId],
    queryFn: () => api.locations.tree(siteId!),
    enabled: !!siteId && (open || !!value),
    staleTime: 60_000,
  })
  const tree: LocationTreeNode[] = Array.isArray(treeQ.data) ? treeQ.data : []

  // Resolve `value` (id) → node so the trigger can render the picked
  // location's name + type. Walks the loaded tree; returns null while
  // the tree is loading (trigger falls back to placeholder in that
  // case).
  const selectedNode = useMemo<LocationTreeNode | null>(() => {
    if (!value) return null
    function find(nodes: LocationTreeNode[]): LocationTreeNode | null {
      for (const n of nodes) {
        if (n.id === value) return n
        const c = find(n.children ?? [])
        if (c) return c
      }
      return null
    }
    return find(tree)
  }, [tree, value])

  function handleOpen() {
    if (disabled || !siteId) return
    setOpen(true)
    setQuery('')
  }

  function handlePick(id: string) {
    onChange(id)
    setOpen(false)
    setQuery('')
  }

  return (
    <>
      {/* Trigger — a read-only TextField styled to match SearchableSelect
          so it drops into existing forms without a visual shift. */}
      <TextField
        ref={anchorRef}
        size="small" fullWidth
        value={selectedNode?.name ?? ''}
        placeholder={placeholder ?? (siteId ? 'Select location (optional)' : 'Pick a site first')}
        onClick={handleOpen}
        disabled={disabled || !siteId}
        InputProps={{
          readOnly: true,
          sx: { cursor: (disabled || !siteId) ? 'not-allowed' : 'pointer' },
          startAdornment: selectedNode ? (
            <InputAdornment position="start">
              <PlaceOutlinedIcon sx={{ fontSize: 16, color: '#0D9488' }} />
            </InputAdornment>
          ) : undefined,
          endAdornment: (
            <InputAdornment position="end">
              {selectedNode && !disabled && (
                <Box
                  component="span"
                  onClick={(e) => { e.stopPropagation(); onChange('') }}
                  sx={{
                    display: 'inline-flex', cursor: 'pointer',
                    color: 'text.secondary', p: 0.25, mr: 0.5,
                    '&:hover': { color: 'text.primary' },
                  }}
                  aria-label="Clear location"
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </Box>
              )}
              {open
                ? <KeyboardArrowUpIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                : <KeyboardArrowDownIcon sx={{ fontSize: 20, color: 'text.secondary' }} />}
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': { fontSize: 13.5, pr: 0.5 },
          '& .MuiOutlinedInput-input': { cursor: 'inherit' },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => { setOpen(false); setQuery('') }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: anchorRef.current?.clientWidth ?? 320,
              maxHeight: 380, mt: 0.5, borderRadius: 2,
              display: 'flex', flexDirection: 'column',
            },
          },
        }}
      >
        {/* Search — filters against every node name in the tree. Matching
            nodes' ancestors auto-expand so hits deep in the hierarchy
            surface without the user having to tap through parents. */}
        <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            size="small" fullWidth autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search locations"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 } }}
          />
        </Box>

        {/* Body — loading, empty, or the recursive tree. */}
        <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
          {treeQ.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={20} />
            </Box>
          ) : tree.length === 0 ? (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary',
              textAlign: 'center', py: 3 }}>
              No locations under this site
            </Typography>
          ) : (
            <TreeBody
              nodes={tree}
              selectedId={value}
              query={query.trim().toLowerCase()}
              onPick={handlePick}
            />
          )}
        </Box>
      </Popover>
    </>
  )
}

/// Renders the top-level nodes; each row recurses via `_TreeRow`.
function TreeBody({
  nodes, selectedId, query, onPick,
}: {
  nodes: LocationTreeNode[]
  selectedId: string
  query: string
  onPick: (id: string) => void
}) {
  return (
    <Stack sx={{ px: 0.5 }}>
      {nodes.map((n) => (
        <TreeRow key={n.id} node={n} depth={0}
          selectedId={selectedId} query={query} onPick={onPick} />
      ))}
    </Stack>
  )
}

function TreeRow({
  node, depth, selectedId, query, onPick,
}: {
  node: LocationTreeNode
  depth: number
  selectedId: string
  query: string
  onPick: (id: string) => void
}) {
  // Ancestors of the current selection auto-expand on mount so the
  // selected row is immediately visible when the popover opens.
  const [expanded, setExpanded] = useState(() =>
    containsId(node, selectedId) || (query !== '' && subtreeMatches(node, query))
  )
  // Re-expand when the search query changes to include a deeper match.
  useEffect(() => {
    if (query !== '' && subtreeMatches(node, query)) setExpanded(true)
  }, [query, node])

  // Under a search query, whole subtrees with no hit collapse to nothing.
  if (query !== '' && !subtreeMatches(node, query)) return null

  const hasChildren = (node.children?.length ?? 0) > 0
  const isSelected = node.id === selectedId
  const isDirectHit = query !== ''
    && node.name.toLowerCase().includes(query)

  return (
    <>
      <Box
        onClick={() => onPick(node.id)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          pl: 0.5 + depth * 1.5, pr: 0.75, py: 0.5,
          borderRadius: 1, cursor: 'pointer',
          bgcolor: isSelected
            ? (t) => `${t.palette.primary.main}14`
            : isDirectHit
              ? (t) => t.palette.mode === 'light' ? '#FFFBEB' : 'rgba(217,119,6,0.08)'
              : 'transparent',
          '&:hover': {
            bgcolor: isSelected
              ? (t) => `${t.palette.primary.main}20`
              : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)',
          },
        }}
      >
        <Box
          onClick={(e) => {
            e.stopPropagation()
            if (hasChildren) setExpanded((v) => !v)
          }}
          sx={{
            width: 20, height: 20, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center',
            color: 'text.secondary', flexShrink: 0,
            opacity: hasChildren ? 1 : 0,
            cursor: hasChildren ? 'pointer' : 'default',
          }}
        >
          {expanded
            ? <ExpandMoreIcon sx={{ fontSize: 16 }} />
            : <ChevronRightIcon sx={{ fontSize: 16 }} />}
        </Box>
        <PlaceOutlinedIcon sx={{
          fontSize: 15, color: isSelected ? 'primary.main' : '#0D9488', flexShrink: 0,
        }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: 13, fontWeight: isSelected ? 700 : 500,
            color: isSelected ? 'primary.main' : 'text.primary',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {node.name}
          </Typography>
        </Box>
        {node.type && node.type !== 'other' && (
          <Typography sx={{
            fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
            textTransform: 'uppercase', color: 'text.disabled',
            flexShrink: 0,
          }}>
            {node.type}
          </Typography>
        )}
      </Box>
      {hasChildren && expanded && (
        <>
          {(node.children ?? []).map((child) => (
            <TreeRow key={child.id} node={child} depth={depth + 1}
              selectedId={selectedId} query={query} onPick={onPick} />
          ))}
        </>
      )}
    </>
  )
}

/// True when `id` is `root.id` or one of its descendants — used to
/// decide whether a row should auto-expand on mount so the selected
/// leaf is visible.
function containsId(root: LocationTreeNode, id: string): boolean {
  if (!id) return false
  if (root.id === id) return true
  for (const c of root.children ?? []) {
    if (containsId(c, id)) return true
  }
  return false
}

/// True when this node OR any descendant's name contains the query.
/// Case-insensitive; `query` is already lowercased by the caller.
function subtreeMatches(node: LocationTreeNode, query: string): boolean {
  if (query === '') return true
  if (node.name.toLowerCase().includes(query)) return true
  for (const c of node.children ?? []) {
    if (subtreeMatches(c, query)) return true
  }
  return false
}
